import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { EXIT_DIALOG_COPY } from './wizardCopy';

/** Which of the dialog's choices is running. */
export type ExitAction = 'save' | 'discard';

/**
 * "Exit onboarding?" (spec:146; web.md §4.3): finished steps are saved, and Brand Setup can be continued
 * from the Edit page. Save and exit saves the step first; Exit without saving drops its changes; both go
 * to the Edit page. Continue (also Escape, the backdrop and ✕) stays on the step. While a choice runs
 * nothing else can be chosen; one that failed says why here and the dialog stays.
 */
export function ExitOnboardingDialog({
  open,
  busy,
  error,
  onSaveAndExit,
  onExitWithoutSaving,
  onContinue,
  returnFocusTo,
}: {
  open: boolean;
  busy: ExitAction | null;
  error: string | null;
  onSaveAndExit: () => void;
  onExitWithoutSaving: () => void;
  onContinue: () => void;
  returnFocusTo?: HTMLElement | null;
}) {
  const running = busy !== null;
  return (
    <Modal
      open={open}
      onClose={onContinue}
      title={EXIT_DIALOG_COPY.title}
      trapFocus
      returnFocusTo={returnFocusTo}
      closeDisabled={running}
      closeDisabledReason={EXIT_DIALOG_COPY.saving}
      aboveFooter={
        error ? (
          <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
            {error}
          </p>
        ) : null
      }
      footer={
        // Wraps on a phone: three buttons side by side need more than its width.
        <div className="flex w-full flex-wrap justify-end gap-2">
          <Button type="button" variant="plain" disabled={running} onClick={onContinue}>
            {EXIT_DIALOG_COPY.stay}
          </Button>
          <Button
            type="button"
            variant="secondary"
            loading={busy === 'discard'}
            disabled={running}
            onClick={onExitWithoutSaving}
          >
            {EXIT_DIALOG_COPY.exitWithoutSaving}
          </Button>
          <Button type="button" loading={busy === 'save'} disabled={running} onClick={onSaveAndExit}>
            {EXIT_DIALOG_COPY.saveAndExit}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-ink-label">{EXIT_DIALOG_COPY.body}</p>
    </Modal>
  );
}
